/**
 * Angebotsliste.
 *
 * Ein Angebot läuft ab, weil ein Datum verstrichen ist — nicht weil ein
 * nächtlicher Prozess es umgesetzt hat. Die API bestimmt das beim Lesen und
 * liefert `abgelaufen` mit; angezeigt wird es wie ein Status.
 */
import { useMemo, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Seite } from '../bausteine/Layout';
import { Marker } from '../bausteine/Marker';
import { Fehlermeldung } from '../bausteine/Meldung';
import { Laedt, Leer } from '../bausteine/Zustand';
import { useAbruf } from '../lib/abruf';
import { alsAbfrage } from '../lib/api';
import { datum } from '../lib/format';
import { euro } from '../lib/geld';
import type { AngebotListe } from '../lib/typen';

export function Angebote(): ReactNode {
  const [filter, setzeFilter] = useSearchParams();
  const lese = (name: string): string => filter.get(name) ?? '';

  function aendere(name: string, wert: string): void {
    const neu = new URLSearchParams(filter);
    if (wert === '') neu.delete(name);
    else neu.set(name, wert);
    setzeFilter(neu, { replace: true });
  }

  const pfad = useMemo(
    () => `/api/angebote${alsAbfrage({ status: lese('status'), offen: lese('offen'), proSeite: 100 })}`,
    [filter.toString()],
  );

  const liste = useAbruf<AngebotListe>(pfad);

  return (
    <Seite
      titel="Angebote"
      untertitel={liste.daten ? `${liste.daten.gesamt} insgesamt` : undefined}
      aktionen={
        <Link to="/angebote/neu" className="knopf haupt">
          Angebot schreiben
        </Link>
      }
    >
      <div className="karte">
        <div className="filterleiste">
          <div className="feld">
            <label htmlFor="status">Status</label>
            <select id="status" value={lese('status')} onChange={(e) => aendere('status', e.target.value)}>
              <option value="">alle</option>
              <option value="entwurf">Entwurf</option>
              <option value="versendet">Versendet</option>
              <option value="angenommen">Angenommen</option>
              <option value="abgelehnt">Abgelehnt</option>
            </select>
          </div>
          <div className="feld">
            <label htmlFor="offen">Entscheidung</label>
            <select id="offen" value={lese('offen')} onChange={(e) => aendere('offen', e.target.value)}>
              <option value="">alle</option>
              <option value="true">noch offen</option>
            </select>
          </div>
        </div>

        {liste.fehler ? (
          <div className="karte-koerper">
            <Fehlermeldung text={liste.fehler} />
          </div>
        ) : !liste.daten ? (
          <Laedt was="Angebote werden geladen" />
        ) : liste.daten.angebote.length === 0 ? (
          <Leer
            titel="Kein Angebot"
            hinweis="Ein angenommenes Angebot lässt sich mit einem Klick in eine Rechnung umwandeln."
          />
        ) : (
          <div className="tabelle-rahmen">
            <table className="tabelle">
              <thead>
                <tr>
                  <th className="schmal">Nummer</th>
                  <th className="schmal">Datum</th>
                  <th>Empfänger</th>
                  <th className="schmal">Gültig bis</th>
                  <th className="zahl">Betrag</th>
                  <th className="schmal">Status</th>
                </tr>
              </thead>
              <tbody>
                {liste.daten.angebote.map((a) => (
                  <tr key={a.id}>
                    <td className="schmal fest">
                      <Link to={`/angebote/${a.id}`}>{a.nummer ?? 'Entwurf'}</Link>
                    </td>
                    <td className="schmal">{datum(a.angebotsdatum)}</td>
                    <td>
                      <span className="haupttext">{a.empfaenger ?? '—'}</span>
                      {a.betreff ? <div className="nebentext">{a.betreff}</div> : null}
                      {a.rechnungId ? (
                        <div className="nebentext">
                          <Link to={`/rechnungen/${a.rechnungId}`}>zur Rechnung</Link>
                        </div>
                      ) : null}
                    </td>
                    <td className="schmal">{datum(a.gueltigBis)}</td>
                    <td className="zahl">{euro(a.betragBrutto, false)}</td>
                    <td className="schmal">
                      <Marker status={a.abgelaufen ? 'abgelaufen' : a.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Seite>
  );
}
